import { useSyncExternalStore } from 'react';

export const DISPLAY_PREFERENCES_STORAGE_KEY = 'portfolio-pm-display-preferences';

export type DisplayDensity = 'compact' | 'standard' | 'comfortable';
export type VisualTheme = 'cool-mist' | 'sage-daylight' | 'ink-sky' | 'lilac-slate';

export interface DisplayPreferences {
  visualTheme: VisualTheme;
  density: DisplayDensity;
  highContrast: boolean;
  showMobileTaskEstimates: boolean;
  reducedMotion: boolean;
  reducedMotionExplicit: boolean;
  sidebarWidth: number;
  taskDetailWidth: number;
}

export const DEFAULT_DISPLAY_PREFERENCES: DisplayPreferences = {
  visualTheme: 'cool-mist',
  density: 'standard',
  highContrast: false,
  showMobileTaskEstimates: false,
  reducedMotion: false,
  reducedMotionExplicit: false,
  sidebarWidth: 180,
  taskDetailWidth: 380,
};

const DISPLAY_PREFERENCES_EVENT = 'portfolio-pm-display-preferences-change';

let cachedRaw: string | null | undefined;
let cachedNativeReducedMotion: boolean | undefined;
let cachedPreferences: DisplayPreferences | undefined;

export const DISPLAY_LIMITS = {
  sidebarWidth: { min: 160, max: 280 },
  taskDetailWidth: { min: 320, max: 520 },
} as const;

type DisplayPreferencePatch = Partial<DisplayPreferences>;

export function getDisplayPreferences(): DisplayPreferences {
  const nativeReducedMotion = prefersReducedMotion();
  const raw =
    typeof window === 'undefined'
      ? undefined
      : window.localStorage.getItem(DISPLAY_PREFERENCES_STORAGE_KEY);
  if (cachedPreferences && cachedRaw === raw && cachedNativeReducedMotion === nativeReducedMotion) {
    return cachedPreferences;
  }

  const fallback = {
    ...DEFAULT_DISPLAY_PREFERENCES,
    reducedMotion: nativeReducedMotion,
  };

  try {
    if (!raw) return cachePreferences(raw, nativeReducedMotion, fallback);
    const parsed = JSON.parse(raw) as Partial<DisplayPreferences>;
    const reducedMotionExplicit = parsed.reducedMotionExplicit === true;
    return cachePreferences(
      raw,
      nativeReducedMotion,
      normalizeDisplayPreferences({
        ...fallback,
        ...parsed,
        reducedMotion: reducedMotionExplicit ? parsed.reducedMotion === true : nativeReducedMotion,
        reducedMotionExplicit,
      }),
    );
  } catch {
    return cachePreferences(raw, nativeReducedMotion, fallback);
  }
}

export function updateDisplayPreferences(patch: DisplayPreferencePatch): DisplayPreferences {
  const current = getDisplayPreferences();
  const next = normalizeDisplayPreferences({ ...current, ...patch });
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(DISPLAY_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
    cachedRaw = window.localStorage.getItem(DISPLAY_PREFERENCES_STORAGE_KEY);
    cachedNativeReducedMotion = prefersReducedMotion();
    cachedPreferences = next;
    window.dispatchEvent(new Event(DISPLAY_PREFERENCES_EVENT));
  }
  return next;
}

export function resetDisplayPreferences(): DisplayPreferences {
  const next = {
    ...DEFAULT_DISPLAY_PREFERENCES,
    reducedMotion: prefersReducedMotion(),
  };
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(DISPLAY_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
    cachedRaw = window.localStorage.getItem(DISPLAY_PREFERENCES_STORAGE_KEY);
    cachedNativeReducedMotion = prefersReducedMotion();
    cachedPreferences = next;
    window.dispatchEvent(new Event(DISPLAY_PREFERENCES_EVENT));
  }
  return next;
}

export function resetDisplayPreferencesForTests(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(DISPLAY_PREFERENCES_STORAGE_KEY);
  }
  cachedRaw = undefined;
  cachedNativeReducedMotion = undefined;
  cachedPreferences = undefined;
}

export function useDisplayPreferences(): {
  preferences: DisplayPreferences;
  updatePreferences(patch: DisplayPreferencePatch): void;
  resetPreferences(): void;
} {
  const preferences = useSyncExternalStore(subscribe, getDisplayPreferences, getDisplayPreferences);
  return {
    preferences,
    updatePreferences: (patch) => {
      updateDisplayPreferences(patch);
    },
    resetPreferences: () => {
      resetDisplayPreferences();
    },
  };
}

function subscribe(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const notify = (): void => listener();
  window.addEventListener(DISPLAY_PREFERENCES_EVENT, notify);
  window.addEventListener('storage', notify);
  return () => {
    window.removeEventListener(DISPLAY_PREFERENCES_EVENT, notify);
    window.removeEventListener('storage', notify);
  };
}

function normalizeDisplayPreferences(input: DisplayPreferences): DisplayPreferences {
  return {
    visualTheme: isVisualTheme(input.visualTheme)
      ? input.visualTheme
      : DEFAULT_DISPLAY_PREFERENCES.visualTheme,
    density: isDensity(input.density) ? input.density : DEFAULT_DISPLAY_PREFERENCES.density,
    highContrast: input.highContrast === true,
    showMobileTaskEstimates: input.showMobileTaskEstimates === true,
    reducedMotion: input.reducedMotion === true,
    reducedMotionExplicit: input.reducedMotionExplicit === true,
    sidebarWidth: clamp(
      Number(input.sidebarWidth),
      DISPLAY_LIMITS.sidebarWidth.min,
      DISPLAY_LIMITS.sidebarWidth.max,
      DEFAULT_DISPLAY_PREFERENCES.sidebarWidth,
    ),
    taskDetailWidth: clamp(
      Number(input.taskDetailWidth),
      DISPLAY_LIMITS.taskDetailWidth.min,
      DISPLAY_LIMITS.taskDetailWidth.max,
      DEFAULT_DISPLAY_PREFERENCES.taskDetailWidth,
    ),
  };
}

function cachePreferences(
  raw: string | null | undefined,
  nativeReducedMotion: boolean,
  preferences: DisplayPreferences,
): DisplayPreferences {
  cachedRaw = raw;
  cachedNativeReducedMotion = nativeReducedMotion;
  cachedPreferences = preferences;
  return preferences;
}

function isVisualTheme(value: unknown): value is VisualTheme {
  return (
    value === 'cool-mist' ||
    value === 'sage-daylight' ||
    value === 'ink-sky' ||
    value === 'lilac-slate'
  );
}

function isDensity(value: unknown): value is DisplayDensity {
  return value === 'compact' || value === 'standard' || value === 'comfortable';
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
